import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import { RootState } from './store';
import { Mutex } from 'async-mutex';
import { logout, setAccessRefreshToken } from './slices/authSlice';
import { makeTokenRenew } from '../security/keycloak';

const baseUrl = `${import.meta.env.VITE_APP_API}`;

// Mutex para evitar múltiplas renovações simultâneas
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl,

  prepareHeaders: async (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

const customFetchBase: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Aguardar qualquer renovação de token em andamento
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  // Se receber 401 (Unauthorized), tentar renovar o token
  if (result.error && result.error.status === 401) {
    // Verificar se já não há uma renovação em andamento
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Obter o refresh token do estado atual
        const refreshToken = (api.getState() as RootState).auth.refreshToken;

        if (refreshToken) {
          try {
            // Tentar renovar o token
            const renewResponse = await makeTokenRenew(refreshToken);

            if (renewResponse.access_token && renewResponse.refresh_token) {
              // Atualizar os tokens no estado
              api.dispatch(
                setAccessRefreshToken({
                  accessToken: renewResponse.access_token,
                  refreshToken: renewResponse.refresh_token
                })
              );

              // Tentar a requisição original novamente com o novo token
              result = await baseQuery(args, api, extraOptions);
            } else {
              // Falha na renovação, fazer logout
              api.dispatch(logout());
              window.location.href = '/';
            }
          } catch (error) {
            // Erro na renovação, fazer logout
            api.dispatch(logout());
            window.location.href = '/';
          }
        } else {
          // Não há refresh token, fazer logout
          api.dispatch(logout());
          window.location.href = '/';
        }
      } finally {
        release();
      }
    } else {
      // Aguardar a renovação em andamento e tentar a requisição novamente
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export default customFetchBase;
